import React from 'react';
import { Box, Group, Tooltip, UnstyledButton } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { buildFieldDebugTooltipLines, type FieldDebugTooltipContext } from './entityFieldDebugUtils';

export interface FieldWithDebugTooltipProps {
  fieldName: string;
  children: React.ReactNode;
  rendererKey?: string;
  isStubValue?: boolean;
  showFieldDebug: boolean;
  debugContext: FieldDebugTooltipContext;
}

/**
 * Wraps field content with optional data-renderer attributes and a debug info icon when enabled.
 */
export const FieldWithDebugTooltip: React.FC<FieldWithDebugTooltipProps> = ({
  fieldName,
  children,
  rendererKey,
  isStubValue,
  showFieldDebug,
  debugContext,
}) => {
  const withRenderer =
    rendererKey != null ? (
      <Box component="span" style={{ display: 'contents' }} data-renderer={rendererKey} data-field-name={fieldName}>
        {children}
      </Box>
    ) : (
      children
    );

  if (!showFieldDebug) return <>{withRenderer}</>;

  const lines = buildFieldDebugTooltipLines(fieldName, debugContext, rendererKey, isStubValue);
  const tooltipContent = (
    <Box
      component="span"
      display="block"
      ff="monospace"
      fz="xs"
      maw={320}
      style={{ whiteSpace: 'pre-wrap' }}
    >
      {lines.join('\n')}
    </Box>
  );

  const handleCopyDebug = () => {
    void navigator.clipboard.writeText(lines.join('\n'));
  };

  return (
    <Group key={fieldName} gap={4} align="flex-start">
      <Box flex={1} miw={0}>{withRenderer}</Box>
      <Tooltip label={tooltipContent} position="left" withArrow>
        <UnstyledButton
          type="button"
          onClick={handleCopyDebug}
          display="inline-flex"
          aria-label="Copy field debug info"
          mt={6}
          style={{ cursor: 'pointer' }}
        >
          <IconInfoCircle size={18} />
        </UnstyledButton>
      </Tooltip>
    </Group>
  );
};
